import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import {
  FacilityInformation,
  FacilityProcessDetails,
  FacilityState,
} from "../../../sc-models/facility-product-dto";
import FormBuilder from "../../../../../shared/components/form-builder/form-builder";
import { FormConfigProps } from "../../../../../shared/components/form-builder/form-builder.model";
import { dtrackItService } from "../../../../transaction-certificates/tc-service/dtrackit.service";
import { buyerSellerDetailsService } from "../../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { OptionType } from "../../../../../shared/components/form-builder/form-config.model";
import { Utills } from "../../../../../shared/app-configs/utilit";
import { API_ROUTES } from "../../../../../shared/app-configs/sc-app-config";
import { useApi } from "../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import useNotification from "../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { isEmpty } from "lodash";

interface FacilityInfoFormProps {
  formRef: any;
  facilityData: FacilityInformation;
  setFacilityData: React.Dispatch<React.SetStateAction<FacilityInformation>>;
  mfExists?: boolean;
  mainSite?: FacilityState;
  setSubsequentFacilityFlag: React.Dispatch<React.SetStateAction<boolean>>;
  subsequentFacilityFlag: boolean;
  setScSubsequentFacilityButtonFlag: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  scNoOfFarmsFlag: boolean;
  setScNoOfFarmsFlag: React.Dispatch<React.SetStateAction<boolean>>;
  scSubsequentFacilityButtonFlag: boolean;
  facilityInfoData: FacilityProcessDetails[];
  isEditMode?: boolean; 

}

const FacilityInfoForm: React.FC<FacilityInfoFormProps> = ({
  formRef,
  facilityData,
  setFacilityData,
  mainSite,
  mfExists,
  setSubsequentFacilityFlag,
  subsequentFacilityFlag,
  setScSubsequentFacilityButtonFlag,
  scNoOfFarmsFlag,
  setScNoOfFarmsFlag,
  scSubsequentFacilityButtonFlag,
  facilityInfoData,
  isEditMode = false, 

}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { SC_ID } = useParams();
  const { executeGet } = useApi();
  const [cbLicensingCodes, setCBLicensingCodes] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const { setCountries, setStates, setCBLicensingsCodes } =
    useTransactionCertificateStore();

  const [visible, setVisible] = useState({
    iId: true,
    name: true,
    iIdLabel: facilityData?.iId && facilityData?.iId !== "" ? false : true,
  });

  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);

  const [disable, setDisable] = useState({
    country: true,
    state: true,
    town: true,
    postcode: true,
    address1: true,
  });

  const { notify } = useNotification();

  // State to track selected facility standards and facility type
  const [selectedFacilityStandards, setSelectedFacilityStandards] = useState<any>([]);
  const [facilityType, setFacilityType] = useState<string>(facilityData?.facilityType || "MF");

  const getCountry = async () => {
    try {
      const result = await dtrackItService.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setCountryOptions(countries);
      setCountries(countries);
    } catch (err) {
      notify("Failed to fetch countries", "error");
    }
  };

  const getState = async (countryCode: string) => {
    try {
      setIsLoadingStates(true);
      const result = await dtrackItService.getStateBasedonCountry(countryCode);
      const states = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setStateOptions(states);
      setStates(states);
    } catch (err) {
      setStateOptions([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const facilityStandardCodes: OptionType[] = [
    { label: "Organic Content Standard (OCS)", value: "OCS" },
    { label: "Global Recycled Standard (GRS)", value: "GRS" },
    { label: "Recycled Claim Standard (RCS)", value: "RCS" },
    { label: "Responsible Down Standard (RDS)", value: "RDS" },
    { label: "Responsible Wool Standard (RWS)", value: "RWS" },
    { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
    { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
    { label: "Content Claim Standard (CCS)", value: "CCS" },
  ];


  const getFacilityStandardOptions = () => {
    if (facilityType === "MF") {
      // For MF: Only show the selected standard(s) as options
      return facilityStandardCodes.filter(standard =>
        selectedFacilityStandards.includes(standard.value.toString())
      );
    } else if (facilityType === "FA" || facilityType === "AS" || facilityType === "CS") {
      if (
        isEditMode &&
        Array.isArray(facilityData?.facilityStandard) &&
        facilityData.facilityStandard.length > 0
      ) {
        return facilityStandardCodes.filter(option =>
          facilityData?.facilityStandard?.includes(option.value.toString())
        );
      } else {
        return facilityStandardCodes;
      }
    } else {
      return facilityStandardCodes;
    }
  };



  const fetchTEDetails = async () => {
    const response: any = await executeGet(API_ROUTES.GET_CO_DETAILS, {
      scId: SC_ID,
    });
    const mappedDetails = {
      account_name: response.data.certifiedOrganizationName,
      TE_ID: response.data.certifiedOrganizationTeId,
      street: response.data.certifiedOrganizationAddress1,
      city: response.data.certifiedOrganizationTown,
      postal_code: response.data.certifiedOrganizationPostcode,
      country: response.data.certifiedOrganizationCountryOrArea,
      state: response.data.certifiedOrganizationStateOrProvince,
    };
    if (response?.certifiedOrganizationTeId) {
      setVisible((prev) => ({
        ...prev,
        iIdLabel: false,
      }));
    }
    return mappedDetails;
  };

  const fetchSCStandardDetails = async () => {
    const result = await executeGet(API_ROUTES.GET_SC_DETAILS, { scId: SC_ID });
    if (result?.data?.scStandardDetails?.length > 0) {
      const selectedStandards = result?.data.scStandardDetails.map(
        (detail: any) => detail.scStandard
      );
      setSelectedFacilityStandards(selectedStandards);
      if (formRef?.current) {
        formRef.current.setFieldValue("facilityStandard", selectedStandards);
      }
      const scNoOfFarms = result?.data.scStandardDetails.filter(
        (detail: any) => detail.scNumberOfFarms > 0
      );
    }
  };

  const fetchTEDetailsForMF = async () => {
    const details = await fetchTEDetails();
    handleSellerTEIdValueChange(details);
  };

  useEffect(() => {
    const getCBLicensingCodes = async () => {
      const result: any = await dtrackItService.getCBLicensingCodes();
      const cbLicensing = result.data.map((t: any) => ({
        value: t.code_1,
        label: t.code_1,
      }));
      setCBLicensingCodes(cbLicensing);
      setCBLicensingsCodes(cbLicensing);
    };
    getCBLicensingCodes();

    if (!mfExists && facilityData?.facilityType === "MF") {
      fetchTEDetailsForMF();
    }
  }, []);

  useEffect(() => {
    if (facilityData?.facilityType === "MF") {
      setShowWarning(true);
    }
    // Set initial facility type from facilityData
    if (facilityData?.facilityType) {
      setFacilityType(facilityData.facilityType);
    }
  }, [facilityData]);

  useEffect(() => {
    if (formRef.current && formRef.current.values?.facilityStandard) {
      const currentStandards = formRef.current.values.facilityStandard;
      setSelectedFacilityStandards(currentStandards);
    }
  }, [formRef.current?.values?.facilityStandard]);

  useEffect(() => {
    if (facilityData?.facilityStandard) {
      const standards = Array.isArray(facilityData.facilityStandard)
        ? facilityData.facilityStandard
        : [facilityData.facilityStandard];
      setSelectedFacilityStandards(standards);
    }
  }, [facilityData?.facilityStandard]);

  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error;
    }
  };

  const handleSellerTEIdValueChange = (value: any) => {
    const fields = [
      "address1",
      "town",
      "postcode",
      "stateProvince",
      "countryArea",
      "name",
      "iId",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setDisable((prev) => ({
        ...prev,
        country: true,
        state: true,
      }));
      setVisible((prev) => ({
        ...prev,
        iIdLabel: true,
      }));
    } else {
      formRef.current.setFieldValue("name", value.account_name || "");
      formRef.current.setFieldValue("iId", value.TE_ID);

      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("address1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue("address1", value.street);
      }

      setScSubsequentFacilityButtonFlag(true);

      if (value.city != null) {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("town", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
        formRef.current.setFieldValue("town", "");
      }

      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue("postcode", value.postal_code);
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("postcode", "");
      }

      if (value.country) {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
        formRef.current.setFieldValue("countryArea", value.country);
        setDisable((prev) => ({
          ...prev,
          country: true,
          state: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          country: false
        }))
      }

      if (value.state) {
        formRef.current.setFieldValue("stateProvince", value.state);
        setDisable((prev) => ({
          ...prev,
          state: true,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          state: false,
        }));
      }

      setVisible((prev) => ({
        ...prev,
        iIdLabel: value?.TE_ID && value?.TE_ID !== "" ? false : true,
      }));
    }
  };

  useEffect(() => {
    if (facilityData?.countryArea) {
      let countryCode = JSON.stringify(
        `[${[facilityData.countryArea].map((item) => `'${item}'`)}]`
      ).replace(/^"|"$/g, "");
      getState(countryCode);
    }
  }, [facilityData?.countryArea]);

  useEffect(() => {
    if (SC_ID) {
      fetchSCStandardDetails();
    }
    getCountry();
  }, []);

  const checkInitialValidations = (scFarmsFlag: boolean) => {
    const farmCategories = ["PR0036", "PR0011", "PR0037", "PR0038"];
    const selectedProcesses = facilityInfoData.filter((process) =>
      farmCategories.includes(process.FacilityProccessCategoryCode)
    );
    if (
      facilityData?.facilityType !== "MF" &&
      scFarmsFlag &&
      selectedProcesses.length > 0
    ) {
      setSubsequentFacilityFlag(true);
      setVisible((prev) => ({
        ...prev,
        name: false,
      }));
      setDisable((prev) => ({
        ...prev,
        postcode: false,
        town: false,
        country: false,
        state: false,
        address1: false,
      }));
      setScSubsequentFacilityButtonFlag(true);
    } else {
      setSubsequentFacilityFlag(false);
    }
  };

  const handleSellerNameValueChange = (value: any) => {
    const fields = [
      "address1",
      "town",
      "postcode",
      "stateProvince",
      "countryArea",
      "name",
      "iId",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });

      setVisible((prev) => ({
        ...prev,
        iId: true,
      }));
    } else {
      formRef.current.setFieldValue("name", value.account_name);
      formRef.current.setFieldValue("iId", value.TE_ID);

      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("address1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue("address1", value.street);
      }

      if (value.city != null) {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("town", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
        formRef.current.setFieldValue("town", "");
      }

      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue("postcode", value.postal_code);
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("postcode", "");
      }

      formRef.current.setFieldValue(
        "countryArea",
        value?.country === null ? "" : value?.country
      );
      setDisable((prev) => ({
        ...prev,
        country: value?.country != null ? true : false,
      }));
      formRef.current.setFieldValue(
        "stateProvince",
        value.state == null ? "" : value.state
      );
      setVisible((prev) => ({
        ...prev,
        iId: false,
      }));
    }
  };

  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setFacilityData(facilityData);
  };

  const formConfig: FormConfigProps = {
    pageName: "sc-facilitydetails",
    initialValues: {
      ...facilityData,
      facilityType: facilityData?.facilityType || "MF",
    },
    validationSchema: Yup.object().shape({
      facilityNo: Yup.string().required("Facility No. is required"),
      ...(mfExists == true
        ? {
          facilityType: Yup.string()
            .test(
              "is-mf",
              "Main Facility already exists. Choose a different facility type.",
              (value: string | undefined, context: Yup.TestContext) => {
                const facilityNo = context.parent.facilityNo;
                return !(
                  value === "MF" &&
                  facilityNo !== mainSite?.facilityInfo?.facilityNo
                );
              }
            )
            .required("Facility Type is required"),
        }
        : { facilityType: Yup.string().required("Facility Type is required") }),
      iId: Yup.string().when("facilityType", {
        is: (value: string) => subsequentFacilityFlag,
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.required("TE-ID is required"),
      }),
      brandNames: Yup.string(),
      facilityStandard: Yup.array()
        .of(Yup.string())
        .min(1, "At least one Facility Standard must be selected")
        .required("Facility Standard is required"),
      name: Yup.string().required("Name is required"),
      address1: Yup.string().required("Address 1 is required"),
      postcode: Yup.string().nullable(),
      town: Yup.string().nullable(),
      countryArea: Yup.string().required("Country/Area is required"),
      stateProvince: Yup.string().required("State/Province is required"),
      subcontractorLicenseNo: Yup.string().when("facilityType", {
        is: "CS",
        then: (schema) =>
          schema.required("Subcontractor License No. is required"),
        otherwise: (schema) => schema.optional(),
      }),
      certificationBody: Yup.string().when("facilityType", {
        is: "CS",
        then: (schema) =>
          schema.required("CB of Independently Certified Subcontractor is required"),
        otherwise: (schema) => schema.optional(),
      }),
      expiryDate: Yup.date().nullable(),
      farmCapacity: Yup.array()
        .test(
          'is-required-for-farm-categories',
          function(value) {
            const farmCategories = ["PR0011", "PR0036", "PR0037", "PR0038"];
            const animalStandards = ["RWS", "RMS", "RAS", "RDS"];
            
            const matchingProcesses = facilityInfoData
              .filter((process) => farmCategories.includes(process.FacilityProccessCategoryCode))
              .map((process) => process.FacilityProccessCategoryCode);
            
            const matchingStandards = selectedFacilityStandards.filter((standard: string) =>
              animalStandards.includes(standard)
            );
            
            const hasFarmCategory = matchingProcesses.length > 0;
            const hasAnimalStandard = matchingStandards.length > 0;
            
            // Only require if both conditions are met and scNoOfFarmsFlag is true
            if (hasFarmCategory && hasAnimalStandard && scNoOfFarmsFlag) {
              if (!value || value.length === 0) {
                const processText = matchingProcesses.join(', ');
                const standardText = matchingStandards.join(', ');
                return this.createError({
                  message: `Facility Farm Capacity is required when facility process category is ${processText} and SC Standard is ${standardText}`
                });
              }
            }
            return true;
          }
        )
        .test(
          'not-invalid-value',
          'Valid Farm Capacity values are required. "n/a, N/A, NULL, null, Not available" are not accepted',
          function(value) {
            if (!value || value.length === 0) return true; // If empty, let the required test handle it
            
            const invalidValues = ['n/a', 'n\\a', 'null', 'not available', 'nil'];
            
            const hasInvalidValue = value.some((item: string) => {
              if (!item) return false;
              const trimmedValue = item.trim().toLowerCase();
              return invalidValues.some(invalid => trimmedValue === invalid);
            });
            
            return !hasInvalidValue;
          }
        ),
    }),
    sections: [
      {
        title: "Facility Information",
        fields: [
          {
            label: "Facility No.",
            name: "facilityNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: { placeholder: "Enter Facility No." },
          },
          {
            label: "Facility Type",
            name: "facilityType",
            type: "select",
            disabled: !mfExists && facilityData?.facilityType === "MF",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              options: [
                { value: "MF", label: "Main Facility/Main Site (MF)" },
                { value: "FA", label: "Facility/Subsequent Site (FA)" },
                { value: "AS", label: "Associated Subcontractor (AS)" },
                { value: "CS", label: "Independently Certified Subcontractor" },
              ],
              placeholder: "Select Facility Type",
              value: facilityType,
            },
            events: {
              onChange: async (updatedValue: any) => {
                setFacilityType(updatedValue); // Update facility type state
                setShowWarning(updatedValue === "MF");
                setVisible((prev) => ({
                  ...prev,
                  iId: false,
                }));
                if (updatedValue === "MF") {
                  const details = await fetchTEDetails();
                  handleSellerTEIdValueChange(details);
                } else {
                  setVisible((prev) => ({
                    ...prev,
                    iId: true,
                  }));
                  const mappedDetails = {
                    account_name: "",
                    TE_ID: "",
                    street: "",
                    city: "",
                    postal_code: "",
                    country: "",
                    state: "",
                  };
                  handleSellerTEIdValueChange(mappedDetails);
                }
                if (updatedValue !== "MF" && scNoOfFarmsFlag) {
                  setSubsequentFacilityFlag(true);
                  setVisible((prev) => ({
                    ...prev,
                    name: false,
                  }));
                  setDisable((prev) => ({
                    ...prev,
                    postcode: false,
                    town: false,
                    country: false,
                    state: false,
                    address1: false,
                  }));
                  formRef.current?.setFieldValue("countryArea", "");
                  formRef.current?.setFieldValue("stateProvince", "");
                } else {
                  setSubsequentFacilityFlag(false);
                }
              },
            },
          },
          {
            label: "TE-ID",
            name: "iId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => !subsequentFacilityFlag,
            value: facilityData.iId,
            visible: (value: any) => visible.iId,
            props: {
              placeholder: "Search TE-ID",
              variant: "outlined",
              restrictInputFormat: visible?.iIdLabel,
              param: "iId",
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "TE-ID",
            name: "iId",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (values: any) => values.facilityType !== "FA",
            disabled: true,
            visible: (value: any) => !visible.iId,
            props: {
              placeholder: "Enter TE-ID",
              fullWidth: true,
            },
          },
          {
            label: "Name",
            name: "name",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            value: facilityData.name,
            visible: (value: any) => visible.name,
            props: {
              placeholder: "Search Name",
              variant: "outlined",
              param: "name",
              onChange: handleSellerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "name");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Name",
            name: "name",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: visible.name,
            visible: (value: any) => !visible.name,
            props: {
              placeholder: "Enter Name",
              fullWidth: true,
            },
          },
          {
            label: "Address",
            name: "address1",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: disable?.address1,
            props: { placeholder: "Enter Address 1" },
          },
          {
            label: "Postcode",
            name: "postcode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            disabled: disable?.postcode,
            props: { placeholder: "Enter Postcode" },
          },
          {
            label: "Town",
            name: "town",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            disabled: disable?.town,
            props: { placeholder: "Enter Town" },
          },
          {
            label: "Country/Area",
            name: "countryArea",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: disable?.country,
            events: {
              onChange: (newValue: any) => {
                let countryCode = JSON.stringify(
                  `[${[newValue].map((item) => `'${item}'`)}]`
                ).replace(/^"|"$/g, "");
                getState(countryCode);
              },
            },
            props: {
              enableSearch: true,
              options: countryOptions,
              placeholder: "Select Country/Area",
              fullWidth: true,
            },
          },
          {
            label: "State/Province",
            name: "stateProvince",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (values: any) => disable.state,
            props: {
              options: stateOptions,
              enableSearch: true,
              placeholder: "Select State/Province",
              fullWidth: true,
              loading: isLoadingStates,
            },
          },
          {
            label: "Facility Last Audit Date",
            name: "lastAuditDate",
            type: "date",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: { placeholder: "Select Last Audit Date" },
          },
          {
            label: "Facility Standard",
            name: "facilityStandard",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              multiple: true,
              options: getFacilityStandardOptions(),
              placeholder: "Selected Facility Standards",
              value: selectedFacilityStandards,
            },
            events: {
              onChange: (selectedValues: string[]) => {
                if (facilityType === "MF") {
                  // Only allow adding, not removing
                  const newSelections = selectedValues.filter(
                    (val: string) => !selectedFacilityStandards.includes(val)
                  );
                  setSelectedFacilityStandards([
                    ...selectedFacilityStandards,
                    ...newSelections,
                  ]);
                  formRef.current?.setFieldValue(
                    "facilityStandard",
                    [...selectedFacilityStandards, ...newSelections]
                  );
                } else {
                  setSelectedFacilityStandards(selectedValues);
                  formRef.current?.setFieldValue(
                    "facilityStandard",
                    selectedValues
                  );
                }
              },
            } as {
              onChange: (selectedValues: string[]) => void;
            },
          },
          {
            label: "Subcontractor License No.",
            name: "subcontractorLicenseNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            disabled: (values: any) => values?.facilityType !== "CS",
            required: (values: any) => {
              return values.facilityType == "CS" ? true : false;
            },
            visible: (values: any) => values.facilityType === "CS",
            props: { placeholder: "Enter Subcontractor License No." },
          },
          {
            label: "Facility Farm Capacity",
            visible: (values) => scNoOfFarmsFlag && (values?.facilityType === "MF" || values?.facilityType === "FA"),
            name: "farmCapacity",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: (() => {
              const farmCategories = ["PR0011", "PR0036", "PR0037", "PR0038"];
              const animalStandards = ["RWS", "RMS", "RAS", "RDS"];
              
              const hasFarmCategory = facilityInfoData.some((process) =>
                farmCategories.includes(process.FacilityProccessCategoryCode)
              );
              
              const hasAnimalStandard = selectedFacilityStandards.some((standard: string) =>
                animalStandards.includes(standard)
              );
              
              return hasFarmCategory && hasAnimalStandard && scNoOfFarmsFlag;
            })(),
            props: { placeholder: "Enter Farm Capacity" },
          },
          {
            label: "Facility Farm No. Of Animals",
            visible: (values) => scNoOfFarmsFlag && (values?.facilityType === "MF" || values?.facilityType === "FA"),
            name: "farmNoAnimals",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: { placeholder: "Enter Number of Animals" },
          },
          {
            label: "CB of Independently Certified Subcontractor",
            name: "certificationBody",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            visible: (values: any) => values.facilityType === "CS",
            props: {
              options: cbLicensingCodes,
              placeholder: "Select Certification Body",
            },
          },
          {
            label: "Expiry Date of Independently Certified Subcontractor",
            name: "expiryDate",
            type: "date",
            gridSize: { xs: 12, md: 6, lg: 6 },
            disabled: false,
            required: false,
            visible: (values: any) => values.facilityType === "CS",
            props: { placeholder: "Select Expiry Date", disableFuture: false },
          },
        ],
      },
    ],
    handleSubmit: (values) => {
      setFacilityData(values);
      toggleDrawer();
    },
  };

  return (
    <>
      {showWarning && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "#FFF3E0",
            borderRadius: 1,
            border: "1px solid #FFB74D",
          }}
        >
          <Typography color="warning.dark">
            Note: Any changes to the Certified Organization (CO) details should
            be made in the "Certified Organization Details" tab.
          </Typography>
        </Box>
      )}
      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </>
  );
};

export default FacilityInfoForm;